import { DtoStudentAITimeline, DtoTimelineEvent } from '../models/dto-student-ai-timeline';
import { STUDENT_PHOTO_MOCK } from './student-photo.mock';

/**
 * Mock data taken from the client's sample spreadsheet (Mock_AI_Timeline_Data_POC.xlsx).
 *
 * Spreadsheet -> DTO mapping:
 *  - incident_name        -> title (and category: Attendance -> attendance, Wellbeing -> wellbeing,
 *                            Behaviour -> conduct, Attainment -> achievement, Overall -> other)
 *  - incident_description -> description
 *  - incident_severity    -> severityScore
 *  - intervention_id      -> interventionId
 *  - intervention_effectivness HIGH / MEDIUM / LOW -> 'high' / 'medium' / 'minimum'
 *
 * The spreadsheet dates are relative to TODAY() at 4-day intervals, so the mock
 * dates are also calculated from today (most recent row = today). Spelling in
 * the sample text has been corrected.
 */

/** ISO date string for `days` days before today. */
function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

type SpreadsheetRow = Omit<DtoTimelineEvent, 'eventId' | 'eventDate'>;

/** Timeline sheet rows, in spreadsheet order (oldest first). */
const TIMELINE_ROWS: SpreadsheetRow[] = [
  { title: 'Wellbeing', category: 'wellbeing', description: 'Wellbeing survey overall score dropped by 16%.', severityScore: -2, interventionId: 1234113 },
  { title: 'Attendance', category: 'attendance', description: 'Late for morning session.', severityScore: -1, interventionId: 1234111 },
  { title: 'Overall', category: 'other', description: 'Was late for school and then refused to engage in lessons.', severityScore: -2, interventionId: 1234111 },
  { title: 'Attainment', category: 'achievement', description: 'Scored 7% above average in MTC and Phonics tests.', severityScore: 3, interventionId: null },
  { title: 'Behaviour', category: 'conduct', description: 'Received star pupil.', severityScore: 2, interventionId: 1234112 },
  { title: 'Wellbeing', category: 'wellbeing', description: 'Wellbeing survey overall score improved by 23%.', severityScore: 3, interventionId: 1234112 },
  { title: 'Behaviour', category: 'conduct', description: 'Refused to share resources.', severityScore: -1, interventionId: 1234113 },
  { title: 'Wellbeing', category: 'wellbeing', description: 'Crying at break time.', severityScore: -1, interventionId: 1234113 },
  { title: 'Behaviour', category: 'conduct', description: 'Refused to engage in shared story time.', severityScore: -1, interventionId: 1234113 },
  { title: 'Wellbeing', category: 'wellbeing', description: 'Wellbeing survey overall score dropped by 37%.', severityScore: -4, interventionId: 1234113 },
  { title: 'Attendance', category: 'attendance', description: 'Missed 3 days of school.', severityScore: -3, interventionId: 1234111 },
  { title: 'Overall', category: 'other', description: 'Consistent lates and poor engagement.', severityScore: -4, interventionId: 1234111 },
  { title: 'Overall', category: 'other', description: 'Had not done home learning and refused to participate in activities.', severityScore: -4, interventionId: 1234113 },
  { title: 'Overall', category: 'other', description: 'Locked self in bathroom and refused to be in classroom.', severityScore: -4, interventionId: 1234113 },
  { title: 'Attendance', category: 'attendance', description: 'Missed whole week of school.', severityScore: -5, interventionId: 1234111 },
];

export function createStudentAITimelineMock(studentId: string): DtoStudentAITimeline {
  const lastIndex = TIMELINE_ROWS.length - 1;

  return {
    studentId,
    student: {
      fullName: 'Erin Test',
      yearGroup: 'Year 5',
      className: 'Butterflies',
      photoUrl: STUDENT_PHOTO_MOCK,
    },
    aiSummary: {
      paragraphs: [
        {
          segments: [
            {
              text: 'Erin has a habit of being absent from school after conduct incidents of severity -2 or lower.',
              isEmphasised: false,
            },
          ],
        },
        {
          segments: [
            { text: 'Erin has recently had multiple high severity conduct incidents and is therefore ', isEmphasised: false },
            { text: 'likely to be absent soon', isEmphasised: true },
            { text: '.', isEmphasised: false },
          ],
        },
        {
          segments: [
            {
              text: "Overall, Erin's recent actions suggest that Erin does not feel a sense of belonging in school.",
              isEmphasised: false,
            },
          ],
        },
      ],
    },
    timelineEvents: TIMELINE_ROWS.map((row, index) => ({
      eventId: 1001 + index,
      eventDate: daysAgo((lastIndex - index) * 4),
      ...row,
    })),
    interventions: [
      {
        interventionId: 1234111,
        name: 'Breakfast club',
        startDate: '2026-09-17',
        effectiveness: 'high',
        commentary: 'Student is rarely late when booked into breakfast club, but it does not resolve attendance issues.',
      },
      {
        interventionId: 1234112,
        name: 'Star Pupil',
        startDate: '2026-09-21',
        effectiveness: 'medium',
        commentary: 'Had short-term positive impact with increased wellbeing survey score.',
      },
      {
        interventionId: 1234113,
        name: 'Buddy Pairing',
        startDate: '2026-08-31',
        effectiveness: 'minimum',
        commentary: 'Erin has not engaged with being paired with a buddy. Sharing and kindness have not improved.',
      },
    ],
  };
}
